import {
  type Attributes,
  type Span,
  SpanStatusCode,
  type TimeInput,
  trace,
} from '@opentelemetry/api'

import {
  buildLogEvent,
  normalizeError,
  sanitizeLogValue,
  sanitizeMetricContext,
  sanitizeUrl,
  serializeLogEvent,
} from './core'
import {
  classifyMetricError,
  registeredMetricErrorOutcome,
  registerMetricErrorOutcome,
} from './metrics'
import {
  type CanonicalIdPaths,
  createSecretValueRedactor,
  MAX_ERROR_MESSAGE_LENGTH,
  MAX_ERROR_STACK_LENGTH,
  MAX_STRING_LENGTH,
  redactSecretEnvValues,
  truncate,
} from './redaction'
import { incrementTelemetryHealth } from './telemetry-health'
import type { NormalizedError, ObservabilityContext } from './types'

export { isNextPrerenderAbandonedError } from './core'

type RuntimeEnvironment = Record<string, string | undefined>

type NextErrorRequest = {
  method: string
  headers: Record<string, string | Array<string> | undefined>
}

type NextErrorContext = {
  routerKind: string
  routePath: string
  routeType: string
  renderSource?: string
  revalidateReason?: string
}

export function deploymentEnvironment(
  environment: RuntimeEnvironment = process.env
): string {
  return environment.VERCEL_ENV ?? environment.NODE_ENV ?? 'development'
}

export function deploymentVersion(
  environment: RuntimeEnvironment = process.env
): string | undefined {
  return environment.VERCEL_GIT_COMMIT_SHA?.trim() || undefined
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function deploymentHostPattern(value: string | undefined): RegExp | null {
  const host = value
    ?.trim()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
  return host ? new RegExp(`^https://${escapeRegex(host)}(?:/|$)`, 'i') : null
}

export function ownedPropagationUrls(
  environment: RuntimeEnvironment = process.env
): Array<RegExp> {
  return [
    /^https:\/\/(?:[^/]+\.)?brew\.new(?:\/|$)/,
    deploymentHostPattern(environment.VERCEL_URL),
    deploymentHostPattern(environment.VERCEL_PROJECT_PRODUCTION_URL),
  ].filter((value): value is RegExp => value !== null)
}

export function createVercelOtelConfig<TProcessor>(args: {
  serviceName: string
  surface: 'web' | 'admin'
  spanProcessors: Array<'auto' | TProcessor>
  environment?: RuntimeEnvironment
}) {
  const environment = args.environment ?? process.env
  return {
    serviceName: args.serviceName,
    attributes: {
      'service.version': deploymentVersion(environment) ?? 'development',
      'deployment.environment.name': deploymentEnvironment(environment),
      'brew.surface': args.surface,
    },
    traceSampler: 'always_on' as const,
    spanProcessors: args.spanProcessors,
    instrumentationConfig: {
      fetch: {
        propagateContextUrls: ownedPropagationUrls(environment),
        dontPropagateContextUrls: [
          /^https:\/\/[^/]*datadog/i,
          /^https:\/\/[^/]*braintrust/i,
        ],
      },
    },
  }
}

function safeRequestId(
  headers: Record<string, string | Array<string> | undefined>
): string | undefined {
  const value = headers['x-vercel-id'] ?? headers['x-request-id']
  return Array.isArray(value) ? value[0] : value
}

const BENIGN_DIGEST_PREFIXES = [
  'NEXT_HTTP_ERROR_FALLBACK',
  'NEXT_NOT_FOUND',
  'NEXT_REDIRECT',
] as const

/**
 * Next.js / fetch aborts and control-flow throws that `onRequestError`
 * still receives. They are not user-facing breakage — the client navigated
 * away, the stream was cancelled, or `notFound()`/`redirect()` leaked
 * through — so they must not become Vercel `error`-level lines.
 */
const BENIGN_NEXT_MESSAGE =
  /^(?:The operation was aborted\.?|This operation was aborted|The user aborted a request\.?|aborted|AbortError|Connection closed\.?|failed to pipe response|The render was aborted(?: by the server without a reason)?\.?)$/i

/**
 * True for disconnects, cancelled renders, and Next control-flow errors.
 * Real render/route failures stay false so they still log.
 */
export function isBenignNextRequestError(error: unknown): boolean {
  const registered = registeredMetricErrorOutcome(error)
  if (registered) {
    return registered === 'cancelled'
  }
  if (error == null || typeof error !== 'object') {
    return false
  }
  const named = error as { name?: unknown; message?: unknown; digest?: unknown }
  if (named.name === 'AbortError' || named.name === 'ResponseAborted') {
    return true
  }
  const digest = named.digest
  if (
    typeof digest === 'string' &&
    BENIGN_DIGEST_PREFIXES.some((prefix) => digest.startsWith(prefix))
  ) {
    return true
  }
  return (
    typeof named.message === 'string' &&
    BENIGN_NEXT_MESSAGE.test(named.message.trim())
  )
}

const recordedErrors = new WeakMap<
  Span,
  { seen: WeakSet<object>; count: number }
>()

/** Also works with a non-recording span, so handled failures keep their outcome. */
export function hasRecordedSpanError(span: Span): boolean {
  return recordedErrors.has(span)
}

/** Fixed diagnostic fields built here, after all external getters have run. */
function redactErrorAttributes(attributes: Attributes): Attributes {
  const redact = createSecretValueRedactor()
  for (const [key, value] of Object.entries(attributes)) {
    if (typeof value !== 'string') {
      continue
    }
    const redacted = redact(value)
    if (key === 'error.stack') {
      attributes[key] = truncate(redacted, MAX_ERROR_STACK_LENGTH)
    } else if (/^error\.cause\.\d+\.stack$/.test(key)) {
      attributes[key] = redacted.slice(0, MAX_STRING_LENGTH)
    } else {
      attributes[key] = truncate(
        redacted,
        key === 'error.message' || /^error\.cause\.\d+\.message$/.test(key)
          ? MAX_ERROR_MESSAGE_LENGTH
          : MAX_STRING_LENGTH
      )
    }
  }
  return attributes
}

/**
 * Record on the span that owns the operation, independently of the active
 * context. Causes are diagnostic attributes, never metric dimensions. Every
 * SDK mutation is isolated so a broken event processor cannot hide the status.
 */
export function recordSpanError(
  span: Span | undefined,
  error: unknown,
  attributes: Attributes = {},
  time?: TimeInput
): void {
  try {
    if (!span) {
      return
    }
    const state = recordedErrors.get(span) ?? {
      seen: new WeakSet<object>(),
      count: 0,
    }
    recordedErrors.set(span, state)
    const duplicate =
      typeof error === 'object' && error !== null && state.seen.has(error)
    if (typeof error === 'object' && error !== null) {
      state.seen.add(error)
    }
    if (!span.isRecording()) {
      return
    }
    if (duplicate || state.count >= 8) {
      try {
        span.setAttributes(sanitizeSpanAttributes(attributes))
      } catch {
        /* Best effort. */
        incrementTelemetryHealth('span_sdk_failed')
      }
      return
    }
    state.count += 1
    let normalized: NormalizedError
    try {
      normalized = normalizeError(error)
    } catch {
      normalized = { kind: 'Error', message: 'Error details unavailable' }
    }
    const errorAttributes: Attributes = {
      'brew.error.outcome': classifyMetricError(error),
      'error.type': normalized.kind,
      'error.message': normalized.message,
      ...(normalized.stack ? { 'error.stack': normalized.stack } : {}),
      ...(normalized.code ? { 'error.code': normalized.code } : {}),
      ...(normalized.status_code !== undefined
        ? { 'error.status_code': normalized.status_code }
        : {}),
    }
    const causes = [...(normalized.causes ?? [])]
    let causeIndex = 0
    while (causes.length && causeIndex < 3) {
      const cause = causes.shift()!
      const prefix = `error.cause.${causeIndex}`
      errorAttributes[`${prefix}.type`] = cause.kind
      errorAttributes[`${prefix}.message`] = cause.message
      if (cause.stack) {
        // Apply the narrower cause limit only after the final literal pass;
        // later metadata can configure a secret crossing that cutoff.
        errorAttributes[`${prefix}.stack`] = cause.stack
      }
      if (cause.code) {
        errorAttributes[`${prefix}.code`] = cause.code
      }
      if (cause.status_code !== undefined) {
        errorAttributes[`${prefix}.status_code`] = cause.status_code
      }
      causes.push(...(cause.causes ?? []))
      causeIndex += 1
    }
    if (causeIndex) {
      errorAttributes['error.cause.count'] = causeIndex
    }
    try {
      const outcome = classifyMetricError(error)
      const redact = createSecretValueRedactor()
      const exception = {
        name: truncate(redact(normalized.kind), MAX_STRING_LENGTH),
        message: truncate(redact(normalized.message), MAX_ERROR_MESSAGE_LENGTH),
        stack: normalized.stack
          ? truncate(redact(normalized.stack), MAX_ERROR_STACK_LENGTH)
          : normalized.stack,
      }
      // SDK processors receive this sanitized DTO instead of the business
      // exception. Retain the owned semantics across that explicit boundary.
      registerMetricErrorOutcome(exception, outcome)
      // An explicitly timed SDK span otherwise reads Date.now() for this event.
      span.recordException(
        exception,
        time ?? new Date(performance.timeOrigin + performance.now())
      )
    } catch {
      /* Status and attributes still matter if the event processor fails. */
      incrementTelemetryHealth('span_sdk_failed')
    }
    try {
      span.setAttributes({
        ...sanitizeSpanAttributes(attributes),
        ...redactErrorAttributes(errorAttributes),
      })
    } catch {
      /* A processor cannot change the application result. */
      incrementTelemetryHealth('span_sdk_failed')
    }
    try {
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: truncate(
          redactSecretEnvValues(normalized.message),
          MAX_ERROR_MESSAGE_LENGTH
        ),
      })
    } catch {
      /* A processor cannot change the application result. */
      incrementTelemetryHealth('span_sdk_failed')
    }
  } catch {
    // A broken processor must never replace the business error being reported.
    incrementTelemetryHealth('span_sdk_failed')
  }
}

export function recordActiveSpanError(
  error: unknown,
  attributes: Attributes = {}
): void {
  try {
    recordSpanError(trace.getActiveSpan(), error, attributes)
  } catch {
    // Active-context lookup can fail independently of the operation.
    incrementTelemetryHealth('span_sdk_failed')
  }
}

export const MAX_SPAN_ARRAY_STRING_LENGTH = 2048

/** Internal only: local copies of sanitized primitive attributes, never caller objects. */
export function redactOwnedSpanAttributes(attributes: Attributes): Attributes {
  const entries = Object.entries(attributes)
  if (entries.length === 0) {
    return attributes
  }
  const redact = createSecretValueRedactor()
  for (const [key, value] of entries) {
    if (typeof value === 'string') {
      attributes[key] = truncate(redact(value), MAX_STRING_LENGTH)
    } else if (Array.isArray(value)) {
      attributes[key] = value.map((entry) =>
        typeof entry === 'string'
          ? redact(entry).slice(0, MAX_SPAN_ARRAY_STRING_LENGTH)
          : entry
      ) as typeof value
    }
  }
  return attributes
}

const SPAN_ID_PATHS: CanonicalIdPaths = {
  'brew.operation.id': true,
  'brew.org.id': true,
  'brew.brand.id': true,
  'brew.user.id': true,
  'brew.chat.id': true,
  'brew.email.id': true,
  'brew.automation.id': true,
  'brew.execution.id': true,
  'brew.send.id': true,
  'brew.workflow.run.id': true,
  'brew.workflow.step.id': true,
}

/** Reviewed correlation fields only; never enumerate a business payload. */
export function contextSpanAttributes(input: ObservabilityContext): Attributes {
  const context = sanitizeMetricContext(input) ?? {}
  return sanitizeSpanAttributes({
    ...(context.requestId ? { 'http.request.id': context.requestId } : {}),
    ...(context.route ? { 'http.route': sanitizeUrl(context.route) } : {}),
    ...(context.method
      ? { 'http.request.method': context.method.toUpperCase() }
      : {}),
    ...(context.statusCode !== undefined
      ? { 'http.response.status_code': context.statusCode }
      : {}),
    ...(context.operationId
      ? { 'brew.operation.id': context.operationId }
      : {}),
    ...(context.orgId ? { 'brew.org.id': context.orgId } : {}),
    ...(context.brandId ? { 'brew.brand.id': context.brandId } : {}),
    ...(context.userId ? { 'brew.user.id': context.userId } : {}),
    ...(context.chatId ? { 'brew.chat.id': context.chatId } : {}),
    ...(context.emailId ? { 'brew.email.id': context.emailId } : {}),
    ...(context.automationId
      ? { 'brew.automation.id': context.automationId }
      : {}),
    ...(context.executionId
      ? { 'brew.execution.id': context.executionId }
      : {}),
    ...(context.sendId ? { 'brew.send.id': context.sendId } : {}),
    ...(context.workflowName
      ? { 'brew.workflow.name': context.workflowName }
      : {}),
    ...(context.workflowRunId
      ? { 'brew.workflow.run.id': context.workflowRunId }
      : {}),
    ...(context.workflowStepId
      ? { 'brew.workflow.step.id': context.workflowStepId }
      : {}),
    ...(context.workflowAttempt !== undefined
      ? { 'brew.workflow.attempt': context.workflowAttempt }
      : {}),
  })
}

/** Bound and redact custom attributes before they reach any span processor. */
export function sanitizeSpanAttributes(attributes: Attributes): Attributes {
  const result: Attributes = {}
  let hasArrayValues = false
  try {
    const bounded: Attributes = {}
    for (const key of Object.keys(attributes).slice(0, 64)) {
      if (key.length > 128) {
        continue
      }
      const original = attributes[key]
      if (
        original === undefined ||
        (typeof original === 'number' && !Number.isFinite(original))
      ) {
        continue
      }
      if (typeof original === 'string') {
        // Redact complete strings before shortening: a cut credential prefix
        // no longer matches its configured literal value.
        bounded[key] = original
      } else if (Array.isArray(original)) {
        // Invalid array entries can still run getters during generic
        // sanitization, before the primitive-only output filter drops them.
        hasArrayValues = true
        bounded[key] = original.slice(0, 32)
      } else if (
        typeof original === 'number' ||
        typeof original === 'boolean'
      ) {
        bounded[key] = original
      }
    }
    const requestId = bounded['http.request.id']
    const mintedRequestId =
      typeof requestId === 'string' && /^req_[0-9a-f]{32}$/.test(requestId)
    let ids: CanonicalIdPaths = SPAN_ID_PATHS
    if (mintedRequestId) {
      ids = { ...SPAN_ID_PATHS, 'http.request.id': true }
    } else if (requestId !== undefined) {
      ids = Object.fromEntries(
        Object.entries(SPAN_ID_PATHS).filter(
          ([key]) => bounded[key] !== requestId
        )
      )
    }
    const sanitized = sanitizeLogValue(bounded, ids)
    if (
      !sanitized ||
      typeof sanitized !== 'object' ||
      Array.isArray(sanitized)
    ) {
      return result
    }
    for (const [key, value] of Object.entries(sanitized)) {
      if (
        typeof value === 'string' ||
        typeof value === 'boolean' ||
        (typeof value === 'number' && Number.isFinite(value))
      ) {
        result[key] = value
      } else if (Array.isArray(value)) {
        if (value.every((entry) => typeof entry === 'string')) {
          result[key] = (value as Array<string>).map((entry) =>
            entry.slice(0, MAX_SPAN_ARRAY_STRING_LENGTH)
          )
        } else if (value.every((entry) => typeof entry === 'boolean')) {
          result[key] = value as Array<boolean>
        } else if (
          value.every(
            (entry) => typeof entry === 'number' && Number.isFinite(entry)
          )
        ) {
          result[key] = value as Array<number>
        }
      }
    }
  } catch {
    // Invalid attribute getters should not prevent the application callback.
  }
  if (hasArrayValues) {
    try {
      return redactOwnedSpanAttributes(result)
    } catch {
      return {}
    }
  }
  return result
}

/**
 * Runtime-neutral uncaught Next.js boundary for both Node and Edge bundles.
 * It emits one bounded JSON line and annotates the active span without I/O.
 */
export function createNextRequestErrorHandler(args: {
  service: string
  loggerName: string
  message: string
  environment?: RuntimeEnvironment
}) {
  return (
    error: unknown,
    request: NextErrorRequest,
    errorContext: NextErrorContext
  ): void => {
    try {
      if (isBenignNextRequestError(error)) {
        return
      }
      const environment = args.environment ?? process.env
      const digest =
        typeof error === 'object' && error !== null && 'digest' in error
          ? String((error as { digest: unknown }).digest)
          : undefined
      recordActiveSpanError(error, {
        'error.source': 'nextjs.onRequestError',
        'brew.nextjs.route_type': errorContext.routeType,
        ...(digest ? { 'brew.nextjs.error_digest': digest } : {}),
      })
      const event = buildLogEvent({
        // Error reporting must also work inside Next's guarded prerender scope.
        now: new Date(performance.timeOrigin + performance.now()),
        status: 'error',
        event: 'nextjs.request.failed',
        message: args.message,
        service: args.service,
        env: deploymentEnvironment(environment),
        version: deploymentVersion(environment),
        source: 'nextjs',
        loggerName: args.loggerName,
        context: {
          requestId: safeRequestId(request.headers),
          route: errorContext.routePath,
          method: request.method,
        },
        fields: {
          outcome: 'failure',
          routerKind: errorContext.routerKind,
          routeType: errorContext.routeType,
          renderSource: errorContext.renderSource,
          revalidateReason: errorContext.revalidateReason,
          digest,
        },
        error,
      })
      console.error(serializeLogEvent(event))
    } catch {
      // Framework error hooks must not create a second request failure.
    }
  }
}

/** One bounded event for a tracer registration failure; never transports it. */
export function reportInstrumentationRegistrationError(args: {
  service: string
  loggerName: string
  message: string
  error: unknown
  environment?: RuntimeEnvironment
}): void {
  try {
    const environment = args.environment ?? process.env
    console.error(
      serializeLogEvent(
        buildLogEvent({
          status: 'error',
          event: 'observability.otel.registration_failed',
          message: args.message,
          service: args.service,
          env: deploymentEnvironment(environment),
          version: deploymentVersion(environment),
          source: 'nextjs',
          loggerName: args.loggerName,
          fields: { outcome: 'failure' },
          error: args.error,
        })
      )
    )
  } catch {
    // Reporting a telemetry registration failure is itself best effort.
  }
}
